// One key per row of the target permission matrix (1:1). String values keep debug output readable.
// See registry.ts for the role sets. ROUTE_* keys at the bottom are NOT matrix rows — they gate
// route access in main.ts and are derived from the matrix.
export enum PERMISSION {
	// --- Menu components ---
	MENU_NAMES = 'MENU_NAMES',
	MENU_IMAGES = 'MENU_IMAGES',
	MENU_AVAILABILITY = 'MENU_AVAILABILITY',
	MENU_TYPE = 'MENU_TYPE',
	MENU_DESCRIPTIONS = 'MENU_DESCRIPTIONS',
	MENU_ADVANCED_SETTINGS = 'MENU_ADVANCED_SETTINGS',
	MENU_IMPORT = 'MENU_IMPORT',

	// --- Edit functions ---
	MENU_ARCHIVE = 'MENU_ARCHIVE',
	MENU_EDIT = 'MENU_EDIT',
	MENU_DUPLICATE = 'MENU_DUPLICATE',
	MENU_DELETE = 'MENU_DELETE',
	MENU_EXPORT = 'MENU_EXPORT',

	// --- Sections ---
	SECTION_EDIT = 'SECTION_EDIT',
	SECTION_NAMES = 'SECTION_NAMES',
	SECTION_DESCRIPTIONS = 'SECTION_DESCRIPTIONS',

	// --- Edit item ---
	ITEM_NAMES = 'ITEM_NAMES',
	ITEM_IMAGES = 'ITEM_IMAGES',
	ITEM_OUT_OF_STOCK = 'ITEM_OUT_OF_STOCK',
	ITEM_FEATURED = 'ITEM_FEATURED',
	ITEM_UPSELL = 'ITEM_UPSELL',
	ITEM_MEAL_PACKAGE = 'ITEM_MEAL_PACKAGE',
	ITEM_MIN_ORDER_QTY = 'ITEM_MIN_ORDER_QTY',
	ITEM_ASSIGN_TAGS = 'ITEM_ASSIGN_TAGS',
	ITEM_PRICING = 'ITEM_PRICING',
	ITEM_PRICE_OPTIONS = 'ITEM_PRICE_OPTIONS',
	ITEM_PRICE_ON_REQUEST = 'ITEM_PRICE_ON_REQUEST',
	ITEM_NUTRITION_SHEET = 'ITEM_NUTRITION_SHEET',
	ITEM_ALLERGENS = 'ITEM_ALLERGENS',
	ITEM_DIETS = 'ITEM_DIETS',
	ITEM_DESCRIPTIONS = 'ITEM_DESCRIPTIONS',
	ITEM_ACCOUNTING_CATEGORY = 'ITEM_ACCOUNTING_CATEGORY',
	ITEM_HST_REBATE = 'ITEM_HST_REBATE',
	ITEM_TAX_RATES = 'ITEM_TAX_RATES',
	ITEM_PRICE_SCHEDULING = 'ITEM_PRICE_SCHEDULING',

	// --- Modifiers ---
	MODIFIERS = 'MODIFIERS',
	MODIFIER_GROUPS = 'MODIFIER_GROUPS',

	// --- Replicate ---
	REPLICATE = 'REPLICATE',
	REPLICATE_DAILY_OVERWRITE = 'REPLICATE_DAILY_OVERWRITE',

	// --- Tags ---
	CREATE_TAGS = 'CREATE_TAGS',

	// --- Availability tab ---
	AVAILABILITY_HOURS = 'AVAILABILITY_HOURS',
	AVAILABILITY_CLOSED_DATES = 'AVAILABILITY_CLOSED_DATES',

	// --- Orders ---
	ORDER_PAUSE = 'ORDER_PAUSE',
	ORDER_REFUND = 'ORDER_REFUND',
	ORDER_REFUND_REQUEST = 'ORDER_REFUND_REQUEST',
	ORDER_REQUEST_DEPOSIT = 'ORDER_REQUEST_DEPOSIT',
	ORDER_MODIFY = 'ORDER_MODIFY',
	ORDER_CREATE_CATERING = 'ORDER_CREATE_CATERING',
	ORDER_CANCEL_CATERING = 'ORDER_CANCEL_CATERING',
	ORDER_CANCEL_SCHOOL_REFUND = 'ORDER_CANCEL_SCHOOL_REFUND',
	ORDER_IMPORT_SCHOOL = 'ORDER_IMPORT_SCHOOL',

	// --- Orders summary ---
	ORDERS_REPORT = 'ORDERS_REPORT',

	// --- Settings > Operations ---
	SET_CUSTOM_QUESTIONS = 'SET_CUSTOM_QUESTIONS',
	EDIT_INQUIRY_FORM = 'EDIT_INQUIRY_FORM',

	// --- Settings > Fulfillment ---
	SET_DELIVERY = 'SET_DELIVERY',
	SET_ORDERING_LOCATIONS = 'SET_ORDERING_LOCATIONS',

	// --- Settings > Appearance ---
	SET_APPEARANCE = 'SET_APPEARANCE',
	SET_LOCATION_IMAGE = 'SET_LOCATION_IMAGE',
	SET_THEME = 'SET_THEME',
	SET_FONTS = 'SET_FONTS',
	SET_LANGUAGES = 'SET_LANGUAGES',
	SET_MENU_DISPLAY = 'SET_MENU_DISPLAY',

	// --- Settings > Promos ---
	SET_PROMO_CODES = 'SET_PROMO_CODES',

	// --- Settings > Business settings ---
	SET_BUSINESS_SETTINGS = 'SET_BUSINESS_SETTINGS',
	SET_SERVICE_CHARGE = 'SET_SERVICE_CHARGE',
	SET_TAX_RATES_SETTINGS = 'SET_TAX_RATES_SETTINGS',
	SET_MANAGE_ACCOUNTING_CATEGORIES = 'SET_MANAGE_ACCOUNTING_CATEGORIES',
	SET_END_OF_SCHOOL_YEAR = 'SET_END_OF_SCHOOL_YEAR',

	// --- Settings > Subsidies ---
	SET_SUBSIDIES = 'SET_SUBSIDIES',

	// --- Settings > Credits ---
	SET_ASSIGN_CREDITS = 'SET_ASSIGN_CREDITS',

	// --- Row actions ---
	SECTION_ARCHIVE = 'SECTION_ARCHIVE',
	SECTION_DUPLICATE = 'SECTION_DUPLICATE',
	SECTION_MOVE = 'SECTION_MOVE',
	SECTION_DELETE = 'SECTION_DELETE',
	ITEM_ARCHIVE = 'ITEM_ARCHIVE',
	ITEM_DUPLICATE = 'ITEM_DUPLICATE',
	ITEM_MOVE = 'ITEM_MOVE',
	ITEM_DELETE = 'ITEM_DELETE',
	MODIFIER_GROUP_DELETE = 'MODIFIER_GROUP_DELETE',

	// --- Location list ---
	LOCATION_LIST = 'LOCATION_LIST',
	LOCATION_REFRESH_MENU = 'LOCATION_REFRESH_MENU',
	LOCATION_SELECTOR = 'LOCATION_SELECTOR',

	// --- Route guards (NOT matrix rows; derived for main.ts) ---
	ROUTE_MENUS = 'ROUTE_MENUS',
	ROUTE_SETTINGS = 'ROUTE_SETTINGS',
	ROUTE_REPLICATE = 'ROUTE_REPLICATE',
	ROUTE_ORDER_MANAGEMENT = 'ROUTE_ORDER_MANAGEMENT',
	ROUTE_DASHBOARD = 'ROUTE_DASHBOARD',
	ROUTE_CUSTOMIZE = 'ROUTE_CUSTOMIZE',
	ROUTE_ADVANCED = 'ROUTE_ADVANCED',
	ROUTE_MENU_GROUPS = 'ROUTE_MENU_GROUPS',
	ROUTE_CREDITS = 'ROUTE_CREDITS',
	ROUTE_SUBSIDIES = 'ROUTE_SUBSIDIES',
	ROUTE_EVENTS = 'ROUTE_EVENTS',
	ROUTE_INQUIRIES = 'ROUTE_INQUIRIES',
	ROUTE_CATERING_BEO = 'ROUTE_CATERING_BEO',
	ROUTE_ENTERPRISE_MENU_PULL = 'ROUTE_ENTERPRISE_MENU_PULL',
	ROUTE_MULTI_LOCATION_SETTINGS = 'ROUTE_MULTI_LOCATION_SETTINGS',
	ROUTE_MULTI_LOCATION_BULK_EDIT = 'ROUTE_MULTI_LOCATION_BULK_EDIT',

	// --- Missing from Excel (not yet in the source-of-truth permission matrix) ---
	SET_INVOICE_SETTINGS = 'SET_INVOICE_SETTINGS',
	SET_PAYMENT_VALIDATION_RULES = 'SET_PAYMENT_VALIDATION_RULES',
	SET_INTEGRATIONS = 'SET_INTEGRATIONS',
	REPORTING_PORTAL = 'REPORTING_PORTAL',
	ORDERS_STATUS_SCREEN = 'ORDERS_STATUS_SCREEN',
	ORDER_CATERING_DISCOUNT = 'ORDER_CATERING_DISCOUNT',
	ORDER_ADD_OPEN_ITEM = 'ORDER_ADD_OPEN_ITEM',
	ORDER_ITEM_LEVEL_DISCOUNT = 'ORDER_ITEM_LEVEL_DISCOUNT',
	SET_ORDER_PARAMETERS = 'SET_ORDER_PARAMETERS',
	SET_ANNOUNCEMENTS = 'SET_ANNOUNCEMENTS',
	SET_NOTIFICATION_SOUND = 'SET_NOTIFICATION_SOUND',
	CLEAR_STUDENTS_SUBSIDIES = 'CLEAR_STUDENTS_SUBSIDIES',

	// --- Session identity (staff/kiosk — NOT Excel matrix rows) ---
	// These describe what session type the user is, not a feature capability. SESSION_STAFF/KIOSK are
	// flagged `stopAdminBypass` in the registry so the resolver skips the admin bypass automatically — the
	// internal admin role is never mistaken for a terminal. OPERATOR is the inverse: a back-office
	// user who operates the app (everyone except the staff/kiosk terminals), resolved normally.
	OPERATOR = 'OPERATOR',
	SESSION_STAFF = 'SESSION_STAFF',
	SESSION_KIOSK = 'SESSION_KIOSK',

	// --- Internal (NOT a matrix row) ---
	// Sentinel for "internal App8 admin only" checks. Its rule resolves to an empty client-role
	// set, so only the admin bypass in hasPermission.ts grants it. Replaces scattered `roleLevel === ROLES.admin`.
	ADMIN_ONLY = 'ADMIN_ONLY'
}
